import React, { useEffect, useRef, useState } from "react";
import {
    IonModal,
    IonContent,
    IonIcon,
    IonButton,
} from "@ionic/react";
import {
    closeOutline,
    starOutline,
    star,
    callOutline,
    globeOutline,
    locationOutline,
    timeOutline,
} from "ionicons/icons";
import "./PlaceDetailModal.css";

// ✅ Both caches outside everything — module level
const photoCache = new Map<string, string>();
const avatarCache = new Map<string, string>();

interface PlaceDetails {
    name: string;
    formatted_address?: string;
    rating?: number;
    user_ratings_total?: number;
    formatted_phone_number?: string;
    website?: string;
    opening_hours?: {
        open_now?: boolean;
        weekday_text?: string[];
    };
    photos?: { photo_reference: string }[];
    geometry?: {
        location: { lat: number; lng: number };
    };
    reviews?: {
        author_name: string;
        rating: number;
        text: string;
        relative_time_description: string;
        profile_photo_url?: string;
    }[];
}

interface Props {
    isOpen: boolean;
    place: PlaceDetails | null;
    onClose: () => void;
}

// ✅ ReviewAvatar outside PlaceDetailModal — stable, not re-created on render
const ReviewAvatar: React.FC<{ src?: string; alt: string }> = ({ src, alt }) => {
    const [imgSrc, setImgSrc] = useState<string>(src || "/assets/user-avatar.png");

    useEffect(() => {
        setImgSrc(src || "/assets/user-avatar.png");
    }, [src]);

    return (
        <img
            src={imgSrc}
            alt={alt}
            className="review-avatar"
            onError={() => setImgSrc("/assets/user-avatar.png")}
        />
    );
};

const PlaceDetailModal: React.FC<Props> = ({ isOpen, place, onClose }) => {
    const [activePhoto, setActivePhoto] = useState(0);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [loadedIndices, setLoadedIndices] = useState<Set<number>>(new Set([0]));
    const [cachedUrls, setCachedUrls] = useState<Record<number, string>>({});
    const MAX_PHOTOS = 5;

    // ✅ Removed stale imgSrc state that was incorrectly placed here
    const placeholderPhotos = place?.photos?.slice(0, MAX_PHOTOS) || [];

    // Reset when modal opens + pre-populate from cache
    useEffect(() => {
        if (isOpen) {
            setActivePhoto(0);
            setLoadedIndices(new Set([0]));

            const preloaded: Record<number, string> = {};
            placeholderPhotos.forEach((photo, i) => {
                if (photoCache.has(photo.photo_reference)) {
                    preloaded[i] = photoCache.get(photo.photo_reference)!;
                    return;
                }
                const lsCached = localStorage.getItem(`photo_${photo.photo_reference}`);
                if (lsCached) {
                    photoCache.set(photo.photo_reference, lsCached);
                    preloaded[i] = lsCached;
                }
            });
            setCachedUrls(preloaded);
        }
    }, [isOpen]);

    // Load photo when user scrolls to it
    useEffect(() => {
        if (!placeholderPhotos.length) return;

        const photo = placeholderPhotos[activePhoto];
        if (!photo) return;

        // ✅ Already in state — do nothing
        if (cachedUrls[activePhoto]) return;

        const lsKey = `photo_${photo.photo_reference}`;

        // ✅ Check memory cache
        const memoryCached = photoCache.get(photo.photo_reference);
        if (memoryCached) {
            setCachedUrls(prev => ({ ...prev, [activePhoto]: memoryCached }));
            return;
        }

        // ✅ Check localStorage
        const lsCached = localStorage.getItem(lsKey);
        if (lsCached) {
            photoCache.set(photo.photo_reference, lsCached);
            setCachedUrls(prev => ({ ...prev, [activePhoto]: lsCached }));
            return;
        }

        // 🌐 Fetch from backend — last resort
        setLoadedIndices(prev => {
            const next = new Set(prev);
            next.add(activePhoto);
            return next;
        });

        const fetchPhoto = async () => {
            try {
                const response = await fetch(
                    `https://getplacephotourl-lrenkrr2bq-uc.a.run.app/photo?ref=${photo.photo_reference}&maxwidth=800`
                );
                const blob = await response.blob();
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64 = reader.result as string;
                    photoCache.set(photo.photo_reference, base64);
                    try {
                        localStorage.setItem(lsKey, base64);
                    } catch {
                        // localStorage full
                    }
                    setCachedUrls(prev => ({ ...prev, [activePhoto]: base64 }));
                };
                reader.readAsDataURL(blob);
            } catch (err) {
                console.error("Failed to fetch photo:", err);
            }
        };

        fetchPhoto();
    }, [activePhoto, isOpen, cachedUrls]);

    if (!place) return null;

    const handlePhotoScroll = () => {
        if (!scrollRef.current) return;
        const scrollLeft = scrollRef.current.scrollLeft;
        const itemWidth = scrollRef.current.offsetWidth || scrollRef.current.clientWidth;
        if (itemWidth > 0) {
            const index = Math.round(scrollLeft / itemWidth);
            setActivePhoto(index);
        }
    };

    const renderStars = (rating: number) => {
        return Array.from({ length: 5 }, (_, i) => (
            <IonIcon
                key={i}
                icon={i < Math.round(rating) ? star : starOutline}
                className="star-icon"
            />
        ));
    };

    const openMaps = () => {
        if (place.geometry) {
            const { lat, lng } = place.geometry.location;
            window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, "_blank");
        } else {
            window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.name)}`, "_blank");
        }
    };

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onClose}
            initialBreakpoint={0.75}
            breakpoints={[0, 0.75, 1]}
            className="place-detail-modal"
        >
            <IonContent className="place-modal-content" scrollY={true}>
                <div className="place-photo-wrapper">
                    <div className="place-photos-scroll" ref={scrollRef} onScroll={handlePhotoScroll}>
                        {placeholderPhotos.length > 0 ? (
                            placeholderPhotos.map((photo, i) => (
                                <div key={i} className="place-photo-item">
                                    {cachedUrls[i] ? (
                                        <img
                                            src={cachedUrls[i]}
                                            alt={`${place.name} ${i + 1}`}
                                            className="place-photo"
                                        />
                                    ) : (
                                        <div className="place-photo-skeleton" />
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="place-photo-placeholder">
                                <IonIcon icon={locationOutline} />
                            </div>
                        )}
                    </div>

                    <div className="place-photo-overlay" />

                    <IonButton fill="clear" className="close-btn" onClick={onClose}>
                        <IonIcon icon={closeOutline} />
                    </IonButton>

                    {placeholderPhotos.length > 1 && (
                        <div className="photo-counter">
                            {activePhoto + 1} / {placeholderPhotos.length}
                        </div>
                    )}

                    <div className="place-header-text">
                        {place.opening_hours && (
                            <div className={`open-badge-overlay ${place.opening_hours.open_now ? "open" : "closed"}`}>
                                {place.opening_hours.open_now ? "Open Now" : "Closed"}
                            </div>
                        )}
                        <h2 className="place-name">{place.name}</h2>
                        {place.rating && (
                            <div className="place-rating-row">
                                <div className="stars">{renderStars(place.rating)}</div>
                                <span className="rating-number">{place.rating.toFixed(1)}</span>
                                {place.user_ratings_total && (
                                    <span className="rating-total">({place.user_ratings_total.toLocaleString()})</span>
                                )}
                            </div>
                        )}
                    </div>

                    {placeholderPhotos.length > 1 && (
                        <div className="photo-dots">
                            {Array.from({ length: placeholderPhotos.length }, (_, i) => (
                                <div key={i} className={`photo-dot ${i === activePhoto ? "active" : ""}`} />
                            ))}
                        </div>
                    )}
                </div>

                <div className="place-body">
                    <div className="place-info-list">
                        {place.formatted_address && (
                            <div className="place-info-row">
                                <IonIcon icon={locationOutline} className="info-icon" />
                                <span>{place.formatted_address}</span>
                            </div>
                        )}
                        {place.formatted_phone_number && (
                            <div className="place-info-row" onClick={() => window.open(`tel:${place.formatted_phone_number}`)}>
                                <IonIcon icon={callOutline} className="info-icon" />
                                <span className="info-link">{place.formatted_phone_number}</span>
                            </div>
                        )}
                        {place.website && (
                            <div className="place-info-row" onClick={() => window.open(place.website, "_blank")}>
                                <IonIcon icon={globeOutline} className="info-icon" />
                                <span className="info-link">Visit Website</span>
                            </div>
                        )}
                    </div>

                    {/* Reviews */}
                    {place.reviews && place.reviews.length > 0 && (
                        <div className="reviews-section">
                            <div className="section-title">
                                <IonIcon icon={starOutline} className="info-icon" />
                                <span>Reviews</span>
                            </div>
                            <div className="reviews-scroll">
                                {place.reviews.map((review, i) => (
                                    <div key={i} className="review-card">
                                        <div className="review-header">
                                            <ReviewAvatar
                                                src={review.profile_photo_url}
                                                alt={review.author_name}
                                            />
                                            <div className="review-author-info">
                                                <span className="review-author">{review.author_name}</span>
                                                <span className="review-time">{review.relative_time_description}</span>
                                            </div>
                                        </div>
                                        <div className="review-stars">
                                            {Array.from({ length: 5 }, (_, i) => (
                                                <IonIcon
                                                    key={i}
                                                    icon={i < review.rating ? star : starOutline}
                                                    className="star-icon small"
                                                />
                                            ))}
                                        </div>
                                        <p className="review-text">{review.text}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Opening hours */}
                    {place.opening_hours?.weekday_text && (
                        <div className="place-section">
                            <div className="section-title">
                                <IonIcon icon={timeOutline} className="info-icon" />
                                <span>Opening Hours</span>
                            </div>
                            <div className="hours-list">
                                {place.opening_hours.weekday_text.map((day, i) => {
                                    const [label, hours] = day.split(": ");
                                    return (
                                        <div key={i} className="hours-row">
                                            <span className="hours-day">{label}</span>
                                            <span className="hours-time">{hours}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <IonButton mode="ios" fill="outline" expand="block" className="directions-btn" onClick={openMaps}>
                        Get Directions
                    </IonButton>
                </div>
            </IonContent>
        </IonModal>
    );
};

export default PlaceDetailModal;